import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Button, Card, Chip, DataTable, EmptyState, Modal, Pane, PaneHead, PhoneCards, PhoneScreen, SubTabs,
} from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuthStore } from '@/stores/useAuthStore';
import { Permission } from '@/types/user';
import { externalProvidersApi } from '@/services/api/externalProvidersApi';
import { exportRows } from './lib/exportCsv';
import {
  amount,
  dayLabel,
  money,
  usePayPayable,
  usePayables,
  useProviderProfile,
  useAddCredit,
  type Payable,
  type ProviderProfile,
} from './lib/useAccounts';
import { AccountantMenu } from './AccountantMenu';

/**
 * Payables — `g_pay` (9) and `g_payprov` (9b). WHAT THE CLINIC OWES, AND BY
 * WHEN.
 *
 * DELIBERATELY NOT A MONTH. The board's own strip says "every unpaid invoice,
 * whatever month it falls in", and it is right: a payable that drops off the
 * list when the month turns is the one that goes unpaid. So this screen has no
 * period picker — it is a state of the world, not a period of it.
 *
 * A PAYABLE IS AN EXPENSE THAT HAS NOT BEEN PAID. The two screens read the
 * same rows with different questions, which is why the pane's last action is
 * to open the expense line rather than a lookup into a parallel ledger.
 *
 * PAYING IS THE MANAGER'S ACT. The accountant records what is owed and against
 * what; somebody else releases the money, with its reference, like a
 * settlement. The button is absent for whoever cannot do it.
 */

export function PayablesPage() {
  const go = useNavigate();
  const [pickedId, setPicked] = useState<string | null>(null);
  const [paying, setPaying] = useState<Payable | null>(null);
  /* The Directory's "Open in Payables" lands here already searched for them. */
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get('supplier') ?? '');
  const [crediting, setCrediting] = useState(false);
  const [paneTab, setPaneTab] = useState<'invoice' | 'provider'>('invoice');

  const roleCheck = useAuthStore((s) => s.roleCheck);
  const canPay = roleCheck.hasPermission(Permission.PAYOUT_APPROVE);

  const { data, isLoading, isError } = usePayables();
  const pay = usePayPayable();
  const addCredit = useAddCredit();

  usePageMeta({ title: 'Payables' });

  const all = data?.items ?? [];
  const items = query.trim()
    ? all.filter((i) =>
        `${i.supplierName ?? ''} ${i.documentName ?? ''} ${i.description}`
          .toLowerCase()
          .includes(query.trim().toLowerCase()),
      )
    : all;
  const directory = useQuery({
    queryKey: ['accountant', 'directory'],
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: () => externalProvidersApi.list({ withCounts: true }),
  });
  const picked = items.find((i) => i.id === pickedId) ?? null;
  const pickedCredit = pickedId?.startsWith('credit:')
    ? (data?.credits ?? []).find((c) => `credit:${c.id}` === pickedId) ?? null
    : null;
  const profile = useProviderProfile(picked?.supplierName ?? null);


  const rows: TableRow[] = items.map((i) => ({
    id: i.id,
    cells: {
      to: (
        <div style={{ minWidth: 0 }}>
          <p className="t-strong">{i.supplierName ?? 'Not named'}</p>
          <p className="t-cap mut">
            {i.documentName ? i.documentName : 'no invoice reference'}
            {i.sourceKind === 'stock' ? ' · from a delivery' : ''}
          </p>
        </div>
      ),
      forWhat: <span className="t-sec mut">{i.description}</span>,
      amount: <span className="t-strong num">{amount(i.amountMinor)}</span>,
      due: (
        <span>
          {i.overdueDays > 0 ? (
            <Chip kind="danger" small>overdue {i.overdueDays} d</Chip>
          ) : (
            <span className="mut">{i.dueOn ? dayLabel(i.dueOn) : '—'}</span>
          )}
        </span>
      ),
    },
  }));

  /* CREDIT NOTES SIT BENEATH THE INVOICES, negative, until netted. */
  const creditRows: TableRow[] = (data?.credits ?? [])
    .filter((c) => !query.trim() || `${c.supplierName} ${c.note ?? ''}`.toLowerCase().includes(query.trim().toLowerCase()))
    .map((c) => ({
      id: `credit:${c.id}`,
      cells: {
        to: <p className="t-strong">{c.supplierName}</p>,
        forWhat: <span className="t-sec mut">credit note{c.note ? ` · ${c.note}` : ''}</span>,
        amount: <span className="t-strong num grn">−{amount(c.amountMinor)}</span>,
        due: <span className="t-sec mut">nets next invoice</span>,
      },
    }));
  const allRows = [...rows, ...creditRows];

  useShellChrome(
    {
      raw: true,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!picked || !!pickedCredit,
      onPaneClose: () => setPicked(null),
      /* `g_pay` — the desk table as cards, over the table's own rows, so a
         figure can never read differently here. */
      phone: (
        <PhoneScreen
          className="acc-phone"
          title="Payables"
          crumb="Accounts"
          sub="what the clinic owes, and by when"
          menu={(close) => <AccountantMenu door="payables" onClose={close} />}
        >
          {/* `g_e8` — the two figures first. */}
          <div className="kit-stats" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div className="card" style={{ margin: 0, flex: 1 }}>
              <p className="t-ovl mut">Open</p>
              <p className="t-strong mt4">{money(data?.openMinor ?? 0)}</p>
            </div>
            <div className="card" style={{ margin: 0, flex: 1 }}>
              <p className="t-ovl mut">Overdue</p>
              <p className={`t-strong mt4${data?.overdueMinor ? ' red' : ''}`}>{money(data?.overdueMinor ?? 0)}</p>
            </div>
          </div>
          <PhoneCards
            columns={[
              { key: 'to', label: 'To', width: 46 },
              { key: 'forWhat', label: 'For', width: 22 },
              { key: 'amount', label: 'Amount', width: 14 },
              { key: 'due', label: 'Due', width: 18 },
            ]}
            rows={allRows}
            body="to"
            trailing="due"
            hide={['"forWhat"']}
            onOpen={(r) => setPicked(r.id)}
            empty={<EmptyState title="The clinic owes nothing" sub="Every supplier invoice with a due date has been settled." />}
          />
          <Card className="mt12">
            <p className="t-sec">
              Overdue is separated from due because they are different emergencies — and overdue rows
              nag the clinic manager, never the doctors.
            </p>
          </Card>
          <Card kind="accent">
            <p className="t-sec brand">
              Lab and imaging debts come from the Directory — the same providers reception dispatches to.
            </p>
          </Card>
        </PhoneScreen>
      ),
      title: 'Payables',
      sub: 'what the clinic owes, and by when',
      search: 'Search a supplier, an invoice number…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export',
        disabled: !items.length,
        onClick: () =>
          exportRows(
            'payables',
            ['To', 'For', 'Amount', 'Due', 'Overdue (days)', 'Document'],
            items.map((i) => [
              i.supplierName ?? '',
              i.description,
              (i.amountMinor / 100).toFixed(2),
              i.dueOn ?? '',
              String(i.overdueDays),
              i.documentName ?? '',
            ]),
          ),
      },
      pane: pickedCredit ? (
        <Pane
          onClose={() => setPicked(null)}
          head={<PaneHead overline="Selected credit note" title={pickedCredit.supplierName} sub={`−${amount(pickedCredit.amountMinor)} · nets the next invoice`} wrapTitle />}
        >
          <Card>
            <p className="t-ovl mut">Credit note</p>
            <p className="t-sec sec mt4">
              {money(pickedCredit.amountMinor)} · issued {dayLabel(pickedCredit.issuedOn)}
            </p>
            {pickedCredit.note ? <p className="t-cap mut mt8">{pickedCredit.note}</p> : null}
          </Card>
        </Pane>
      ) : picked ? (
        <PayablePane
          item={picked}
          tab={paneTab}
          onTab={setPaneTab}
          provider={profile.data ?? undefined}
          onDirectory={() => go('/directory')}
          canPay={canPay}
          onPay={() => setPaying(picked)}
          onExpense={() =>
            go(`/accountant/expenses?period=${picked.spentOn.slice(0, 7)}&category=${picked.category}&line=${picked.id}`)
          }
          onClose={() => setPicked(null)}
        />
      ) : (
        <Pane
          head={
            <PaneHead
              overline="Selected row"
              title="Nothing picked"
              sub="pick an invoice to see what it is against"
            />
          }
        >
          <div className="emptystate">
            <p className="t-sec">
              Paying is the manager&rsquo;s act — recorded with method and reference, like a
              settlement.
            </p>
          </div>
        </Pane>
      ),
    },
    [picked, pickedCredit, canPay, items.length, pickedId, paneTab, query, directory.data, profile.data, data?.credits],
  );

  return (
    <div className="maie desk acc-root">
      <div className="filterrow">
        <span className="t-sec mut">
          Open items — every unpaid invoice, whatever month it falls in · all branches
        </span>
      </div>

      <div className="dcontent scroll">
        <div className="acc-stats acc-stats-3">
          <div className="card" style={{ margin: 0 }}>
            <p className="t-ovl mut">Open — every unpaid invoice</p>
            <p className="t-dxl mt8">{money(data?.openMinor ?? 0)}</p>
          </div>
          <div className="card" style={{ margin: 0 }}>
            <p className="t-ovl mut">Overdue</p>
            <p className={`t-dxl mt8${data?.overdueMinor ? ' red' : ''}`}>
              {money(data?.overdueMinor ?? 0)}
            </p>
          </div>
          <div className="card" style={{ margin: 0 }}>
            <p className="t-ovl mut">Credit notes</p>
            <p className={`t-dxl mt8${data?.creditsMinor ? ' grn' : ''}`}>
              {data?.creditsMinor ? '−' : ''}
              {money(data?.creditsMinor ?? 0)}
            </p>
            <p className="t-cap mut mt4">
              {data?.credits.length
                ? `${data.credits.length} to net against the next invoice from the same supplier`
                : 'none outstanding'}
            </p>
          </div>
        </div>

        <DataTable
          caption="Open items"
          columns={[
            { key: 'to', label: 'To', width: 46 },
            { key: 'forWhat', label: 'For', width: 22 },
            { key: 'amount', label: 'Amount', width: 14 },
            { key: 'due', label: 'Due', width: 18 },
          ]}
          rows={allRows}
          selectedId={pickedId}
          onSelect={(r) => setPicked(r.id === pickedId ? null : r.id)}
          empty={
            <div className="emptystate">
              {isLoading ? (
                <p className="t-sec mut">Reading what is owed…</p>
              ) : isError ? (
                <p className="t-sec mut">It did not come back. Nothing has been changed.</p>
              ) : (
                <>
                  <p className="es-t">The clinic owes nothing</p>
                  <p className="t-sec mut">
                    Every supplier invoice recorded with a due date has been settled. An expense
                    with no due date was paid when it was recorded and never appears here.
                  </p>
                </>
              )}
            </div>
          }
        />

        {canPay ? (
          <p className="mt12">
            <Button kind="outline" onClick={() => setCrediting(true)}>+ Record a credit note</Button>
          </p>
        ) : null}
        <p className="t-sec mut mt16">
          Overdue is separated from due because they are different emergencies — and overdue rows
          nag the clinic manager, never the doctors.
        </p>
        <Card kind="accent" className="mt12">
          <p className="t-sec brand">
            Lab and imaging debts come from the Directory — the same providers reception dispatches to.
          </p>
        </Card>
        <p className="t-sec mut mt16">
          A payable is an expense that has not been paid yet — the same record, asked a different
          question. Recording what is owed is the accountant&rsquo;s; releasing the money is not.
        </p>
      </div>

      <CreditModal
        open={crediting}
        suppliers={[...new Set(all.map((i) => i.supplierName).filter((n): n is string => !!n))]}
        onClose={() => setCrediting(false)}
        onSave={(supplierName, amountMinor, note) => {
          addCredit.mutate(
            { supplierName, amountMinor, note },
            {
              onSuccess: () => toast.success('Credit note recorded'),
              onError: () => toast.error('It was not recorded.'),
            },
          );
          setCrediting(false);
        }}
      />

      <PayModal
        item={paying}
        onClose={() => setPaying(null)}
        onPay={(reference) => {
          if (!paying) return;
          pay.mutate(
            { id: paying.id, paidReference: reference },
            {
              onSuccess: () => {
                toast.success(`${paying.supplierName ?? 'The invoice'} paid`);
                setPicked(null);
              },
              onError: () => toast.error('It was not recorded as paid.'),
            },
          );
          setPaying(null);
        }}
      />
    </div>
  );
}

function PayablePane({
  item,
  tab,
  onTab,
  provider,
  onDirectory,
  canPay,
  onPay,
  onExpense,
  onClose,
}: {
  item: Payable;
  tab: 'invoice' | 'provider';
  onTab: (t: 'invoice' | 'provider') => void;
  provider?: ProviderProfile;
  onDirectory: () => void;
  canPay: boolean;
  onPay: () => void;
  onExpense: () => void;
  onClose: () => void;
}) {
  const invoice = tab === 'invoice';
  return (
    <Pane
      onClose={onClose}
      head={
        <PaneHead
          overline="Selected row"
          title={invoice ? `${item.supplierName ?? 'Not named'} — ${item.documentName ?? 'no reference'}` : (item.supplierName ?? 'Not named')}
          sub={invoice ? (item.overdueDays ? `overdue ${item.overdueDays} days` : 'within terms') : `supplier${provider?.inDirectory ? ' · Directory' : ' · not in the Directory'}`}
          wrapTitle
        />
      }
      foot={
        invoice ? (
          <>
            <Button kind="outline" style={{ flex: 1 }} onClick={onExpense}>
              Open the expense line
            </Button>
            {canPay ? (
              <Button kind="primary" style={{ flex: 1 }} onClick={onPay}>
                Record payment
              </Button>
            ) : null}
          </>
        ) : (
          <Button kind="outline" style={{ width: '100%' }} onClick={onDirectory}>
            Open in the Directory
          </Button>
        )
      }
    >
      <SubTabs
        label="Payable pane"
        items={[
          { key: 'invoice' as const, label: 'Invoice' },
          { key: 'provider' as const, label: 'Provider' },
        ]}
        active={tab}
        onSelect={onTab}
      />
      {invoice ? (
        <>
          <Card style={item.overdueDays ? { borderColor: 'var(--danger-600)' } : undefined}>
            <p className="t-ovl mut">Invoice</p>
            <p className="t-sec sec mt4">
              {money(item.amountMinor)} · due {item.dueOn ? dayLabel(item.dueOn) : 'no date'}
            </p>
            <p className="t-cap mut mt8">
              {item.documentName
                ? `reference ${item.documentName}`
                : 'no document attached — posted is not proven'}
              {item.spentOn ? ` · recorded ${dayLabel(item.spentOn)}` : ''}
            </p>
          </Card>

          <Card>
            <p className="t-ovl mut">Against</p>
            <p className="t-sec sec mt4">{item.description}</p>
            <p className="t-cap mut mt8">
              {item.sourceKind === 'stock'
                ? 'posted from a stock delivery — the boxes are on the shelf'
                : `recorded by hand under ${item.category}`}
            </p>
          </Card>
        </>
      ) : (
        <>
          <Card>
            <p className="t-ovl mut">Contact</p>
            <p className="t-sec sec mt4">
              {provider?.inDirectory
                ? [provider.contactName, provider.phone, provider.email].filter(Boolean).join(' · ') || 'no contact on file'
                : 'not in the Directory'}
            </p>
          </Card>
          <Card>
            <p className="t-ovl mut">Relationship</p>
            <p className="t-sec sec mt4">
              {provider?.orders ?? 0} order{provider?.orders === 1 ? '' : 's'} · {provider?.returns ?? 0} return{provider?.returns === 1 ? '' : 's'}
            </p>
            {provider?.creditsMinor ? (
              <p className="t-cap mut mt4">{money(provider.creditsMinor)} in credit notes to net</p>
            ) : null}
          </Card>
          <Card>
            <p className="t-ovl mut">We owe</p>
            <p className="t-sec sec mt4">
              <b className="red">{money(provider?.owedMinor ?? 0)}</b>
              {item.documentName ? ` · ${item.documentName}` : ''}
              {provider?.overdueDays ? ` · overdue ${provider.overdueDays} d` : ''}
            </p>
          </Card>
        </>
      )}
    </Pane>
  );
}

function PayModal({
  item,
  onClose,
  onPay,
}: {
  item: Payable | null;
  onClose: () => void;
  onPay: (reference: string) => void;
}) {
  const [reference, setReference] = useState('');
  return (
    <Modal
      open={!!item}
      onClose={onClose}
      title="Record the payment"
      sub={item ? `${item.supplierName ?? 'The supplier'} · ${money(item.amountMinor)}` : undefined}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={reference.trim().length < 2}
            onClick={() => {
              onPay(reference.trim());
              setReference('');
            }}
          >
            Record it
          </button>
        </>
      }
    >
      <p className="t-sec sec">
        This marks the invoice settled and takes it off the open list. It does not move any money —
        the transfer happens in the bank, and this is the record of it.
      </p>
      <label className="t-ovl mut mt12" htmlFor="pay-ref">Reference</label>
      <input
        id="pay-ref"
        className="fld t-strong mt4"
        autoFocus
        placeholder="the transfer reference, as the bank wrote it"
        value={reference}
        onChange={(e) => setReference(e.target.value)}
      />
      <p className="t-cap mut mt8">
        Required, for the same reason a write-off needs a note: a payment nobody can trace is not a
        record of anything.
      </p>
    </Modal>
  );
}

/** Record a credit note — a supplier owes the clinic, usually for a return. */
function CreditModal({
  open,
  suppliers,
  onClose,
  onSave,
}: {
  open: boolean;
  suppliers: string[];
  onClose: () => void;
  onSave: (supplierName: string, amountMinor: number, note?: string) => void;
}) {
  const [who, setWho] = useState('');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const minor = Math.round(Number(value) * 100);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record a credit note"
      sub="a supplier owes the clinic — it nets against their next invoice"
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!who.trim() || !(minor > 0)}
            onClick={() => {
              onSave(who.trim(), minor, note.trim() || undefined);
              setWho('');
              setValue('');
              setNote('');
            }}
          >
            Record it
          </button>
        </>
      }
    >
      <label className="t-ovl mut mt12" htmlFor="cr-who">Supplier</label>
      <input id="cr-who" className="fld t-strong mt4" list="cr-suppliers" value={who} onChange={(e) => setWho(e.target.value)} />
      <datalist id="cr-suppliers">{suppliers.map((n) => <option key={n} value={n} />)}</datalist>
      <label className="t-ovl mut mt12" htmlFor="cr-amt">Amount</label>
      <input id="cr-amt" className="fld t-strong mt4 num" inputMode="decimal" placeholder="0.00" value={value} onChange={(e) => setValue(e.target.value)} />
      <label className="t-ovl mut mt12" htmlFor="cr-note">What for</label>
      <input id="cr-note" className="fld mt4" placeholder="returned lot" value={note} onChange={(e) => setNote(e.target.value)} />
    </Modal>
  );
}
